<template>
  <div 
    class="gmf-horizontal-pills-container" 
    :role="role"
    :aria-label="role === 'tablist' ? 'Navigation tabs' : undefined"
  >
    <div ref="navMenu" class="gmf-horizontal-pills-menu" @scroll.passive="handleScroll">
      <button
        v-for="(item, index) in items"
        :key="item.name"
        @click.stop.prevent="selectItem(index)"
        class="gmf-horizontal-pills-item"
        :class="{ 'gmf-active': isActive(index) }"
        :ref="el => setItemRef(el, item)"
        :role="computedElementRole"
        :aria-selected="isActive(index)"
        :aria-controls="`gmf-tab-${getItemId(item)}`"
        :aria-checked="isActive(index)"
      >
        <slot :item="item" :index="index">
          <template v-if="item.name">
            <Icon v-if="item.icon" :icon="item.icon" />
            <div>{{ item.name }}</div>
            <div class="gmf-horizontal-pills-item-count" v-if="item.count && index !== selectedIndex" v-text="item.count" />
          </template>
        </slot>
      </button>
    </div>
    <div class="gmf-horizontal-pills-container-left" :class="{ 'gmf-active': leftScrollButton }" @click.stop.prevent="move(-1)" role="button" aria-label="Scroll left"><Icon icon="chevron-right" /></div>
    <div class="gmf-horizontal-pills-container-right" :class="{ 'gmf-active': rightScrollButton }" @click.stop.prevent="move(1)" role="button" aria-label="Scroll right"><Icon icon="chevron-right" /></div>
  </div>
</template>
<style>
.gmf-horizontal-pills-container {
  background: var(--gmf-col-lightgray);
  display: flex;
  width: 100%;
  position: relative;
  overflow-x: hidden;
  scrollbar-width: none;
  -ms-overflow-style: none;
}

.gmf-horizontal-pills-menu {
  margin: 0;
  display: flex;
  background: inherit;
  padding: var(--gmf-p-3);
  overflow-x: scroll;
  scrollbar-width: none;
  -ms-overflow-style: none;
  position: relative;
  gap: var(--gmf-p-1);
}

.gmf-horizontal-pills-menu::-webkit-scrollbar, .gmf-horizontal-pills-container::-webkit-scrollbar {
  display: none;
}
.gmf-horizontal-pills-container-left, .gmf-horizontal-pills-container-right {
  cursor: pointer;
  position: absolute;
  border-radius: var(--gmf-rounding) 0 0 var(--gmf-rounding);
  top: 50%;
  width: 0px;
  height: calc(100% - var(--gmf-p-1) * 2);
  transform: translateY(-50%);
  background: var(--gmf-col-lightgray);
  transform-origin: center;
  opacity: 0.7;

  transition: all 0.3s ease;

  display: flex;
  align-items: center;
  justify-content: center;
}
.gmf-horizontal-pills-container-left .gmf-icon, .gmf-horizontal-pills-container-right .gmf-icon {
  width: 50%;
  height: 50%;
}
.gmf-horizontal-pills-container-left.gmf-active, .gmf-horizontal-pills-container-right.gmf-active {
  width: 32px;
}
.gmf-horizontal-pills-container-left {
  left: 0;
  transform: translateY(-50%) scaleX(-1);
}
.gmf-horizontal-pills-container-right {
  right: 0;
}

@media (hover) { /* remove hover on mobile */
  .gmf-horizontal-pills-item:hover {
    background: var(--gmf-col-gray);
  }
}

.gmf-horizontal-pills-item:nth-child(1) {
  margin-left: 0;
}

.gmf-horizontal-pills-link::before {
  content: '';
  margin-right: var(--gmf-p-1);
  height: calc(100% - var(--gmf-p-3) * 2);
  border-left: 1px solid var(--gmf-col-darkgray);
}

.gmf-context .gmf-horizontal-pills-link {
  display: flex;
  border-radius: 0;
  cursor: pointer;
  user-select: none;
  gap: var(--gmf-p-2);
  align-items: center;
  background: inherit;
  margin-right: var(--gmf-p-3);
  color: var(--gmf-col-nearblack);
  font-size: var(--gmf-text-small-font-size);
}
.gmf-context .gmf-horizontal-pills-item {
  flex-shrink: 0;
  cursor: pointer;
  user-select: none;
  align-items: center;
  gap: var(--gmf-p-2);
  display: inline-flex;
  padding: var(--gmf-p-3);
  color: var(--gmf-col-nearblack);
  border-radius: var(--gmf-rounding);
  font-size: var(--gmf-text-small-font-size);
  position: relative;
  background-color: unset;
}

.gmf-context .gmf-horizontal-pills-item.gmf-active {
  background-color: var(--gmf-highlight-col);
  color: var(--gmf-col-white);
}

.gmf-context .gmf-horizontal-pills-item-count {
  background: var(--gmf-notification-color);
  color: var(--gmf-col-white);
  min-width: 14px;
  height: 14px;
  line-height: 14px;
  vertical-align: middle;
  text-align: center;
  border-radius: 99px;
  font-weight: bold;
  font-size: 11px;
}
</style>
<script>
import state from '@/state';
import Icon from '@/components/Icon';
import scrollIntoView from '@/utils/scrollIntoView';
const t = state.t.prefix('profile_tabs');

import scrollPage from '@/utils/scrollPage';

export default {
  components: {
    Icon,
  },
  props: {
    showSocialButton: {
      Type: Boolean,
      default: true,
    },
    items: {
      Type: Array,
      default: [],
    },
    selectedItem: {
      Type: Object,
      default: () => ({
        type: null,
        icon: null,
        name: null,
      })
    },
    role: {
      Type: String,
      default: 'tablist',
    },
  },
  data () {
    return {
      scrollPos: -1,
      selectedIndex: -1,
      itemRefMap: {},
      resizeObserver: null,
    };
  },
  mounted () {
    this.$nextTick(() => {
      // Update the scroll pos on resize (only once).
      this.resizeObserver = new ResizeObserver(this.onResize);
      this.resizeObserver.observe(this.$refs.navMenu);
    });
    if (this.selectedItem) {
      this.selectedIndex = this.getItemIndex(this.selectedItem);
    }
  },
  computed: {
    leftScrollButton () {
      if (this.scrollPos === -1) { // Finns ingen scroll
        return false;
      }
      return this.scrollPos > 0.1;
    },
    rightScrollButton () {
      if (this.scrollPos === -1) { // Finns ingen scroll
        return false;
      }
      return this.scrollPos < 0.9;
    },
    computedElementRole () {
      if (this.role === 'tablist') {
        return 'tab';
      }
      if (this.role === 'list') {
        return 'listitem';
      }
      if (this.role === 'radiogroup') {
        return 'radio';
      }
      return undefined;
    },
  },
  methods: {
    t,
    move (steps) {
      scrollPage(this.$refs.navMenu, steps);
    },
    onResize () {
      this.checkWidth();
      // Disconnect observer to avoid multiple calls. It's only needed once after the element has mounted.
      this.resizeObserver.disconnect();
    },
    handleScroll (ev) {
      const deadzone = 1; // One pixel deadzone in case the scroll width is one pixel too large.
      const scrollLength = (ev.target.scrollWidth - ev.target.clientWidth) - deadzone;
      if (scrollLength <= 0) {
        this.scrollPos = -1;
        return;
      }
      this.scrollPos = ev.target.scrollLeft / scrollLength;
    },
    checkWidth () {
      const navMenu = this.$refs.navMenu;
      if (navMenu) {
        this.handleScroll({ target: navMenu });
      }
    },
    selectItem (index) {
      this.selectedIndex = index;
      this.$emit('update:selectedItem', this.items[index]);
    },
    getItemIndex (item) {
      if (! item) {
        return null;
      }
      for (const i in this.items) {
        const menuItem = this.items[i];
        if (this.getItemId(item) === this.getItemId(menuItem)) {
          return ~~i;
        }
      }
      return null;
    },
    getItemId (item) {
      return item.uuid || item.id || item.value || item.name;
    },
    setItemRef (el, item) {
      this.itemRefMap[this.getItemId(item)] = el;
    },
    isActive (index) {
      return index === this.selectedIndex;
    },
  },
  watch: {
    items () {
      this.$nextTick(() => {
        this.checkWidth();
      });
    },
    /** 
     * Selected item is updated in parent component.
     */
    selectedItem (item) {
      // Update selected index.
      this.selectedIndex = this.getItemIndex(item);

      if (! item) {
        return;
      }

      const el = this.itemRefMap[this.getItemId(item)];
      
      if (el) {
        scrollIntoView(this.$refs.navMenu, el, 'x');
      }
    },
  },
};
</script>